import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { fetchIncentiveTickets, cleanInvalidTickets, deleteIncentiveTicket } from '../../services/incentiveTicketsService';
import { formatOMR } from '../../utils/formatCurrency';
import { useAuth } from '../../contexts/AuthContext';
import { useRBAC } from '../../contexts/RBACContext';

const STATUS_COLORS = {
  'Pending Team Assignment': 'bg-warning/15 text-warning',
  'Calculated': 'bg-blue-500/15 text-blue-400',
  'Pending Approval': 'bg-orange-500/15 text-orange-400',
  'Approved': 'bg-success/15 text-success',
  'On Hold': 'bg-yellow-500/15 text-yellow-400',
  'Ready for Payment': 'bg-purple-500/15 text-purple-400',
  'Paid': 'bg-emerald-500/15 text-emerald-400',
  'Rejected': 'bg-error/15 text-error',
  'Cancelled': 'bg-muted text-muted-foreground',
  'Invalid': 'bg-muted text-muted-foreground',
};

// Role-based status visibility
const getVisibleStatuses = (role) => {
  switch (role) {
    case 'admin':
      return null; // all
    case 'finance':
      return ['Approved', 'Ready for Payment', 'Paid'];
    case 'project_manager':
      return ['Pending Team Assignment', 'Calculated', 'Pending Approval'];
    case 'approver':
      return ['Pending Approval', 'Approved', 'Rejected', 'On Hold'];
    case 'sales':
      return ['Pending Team Assignment', 'Calculated', 'Pending Approval', 'Approved', 'Ready for Payment', 'Paid'];
    default:
      return null;
  }
};

const AllIncentiveTickets = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const { isAdmin, canViewOwn, canViewAll, MODULE_KEYS, isProjectManager, hasPermission, PERM_KEYS } = useRBAC();
  const userRole = userProfile?.role || user?.profile?.role || 'viewer';

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [cleaning, setCleaning] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, ticketNumber, projectName }
  const [deleting, setDeleting] = useState(false);

  const visibleStatuses = getVisibleStatuses(userRole);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const filters = {};
      // Always enforce Close Won filter — no open deals
      // Role-based status filter
      if (statusFilter) {
        filters.status = statusFilter;
      } else if (visibleStatuses) {
        filters.status = visibleStatuses;
      }
      if (search) filters.search = search;
      // View Own: user can only see their own deals (by sales_deal_owner_id)
      const viewOwnOnly = canViewOwn(MODULE_KEYS?.INCENTIVE_TICKETS);
      if (viewOwnOnly && user?.id) {
        filters.salesOwnerId = user?.id;
      } else if (!isAdmin && userRole === 'sales' && user?.id) {
        // Legacy fallback for sales role without explicit RBAC view_own
        filters.salesOwnerId = user?.id;
      }
      const data = await fetchIncentiveTickets(filters);
      // Extra safety: filter out any non-Close-Won rows on the frontend
      const closeWonOnly = data?.filter(t =>
        t?.dealStatus === 'Close Won' || t?.dealStatusSource === 'Close Won'
      );
      setTickets(closeWonOnly);
    } catch (err) {
      console.error('[IncentiveTickets] Load error:', err);
      setError(err?.message || 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search, userRole, user?.id]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  const handleCleanInvalidTickets = async () => {
    if (!isAdmin) return;
    setCleaning(true);
    setError('');
    setSuccessMsg('');
    try {
      const result = await cleanInvalidTickets(user?.id);
      if (result?.markedInvalid === 0) {
        setSuccessMsg('No invalid tickets found. All tickets are linked to Close Won deals.');
      } else {
        setSuccessMsg(`Cleaned ${result?.markedInvalid} invalid ticket(s). They are now hidden from all normal views.`);
      }
      await loadTickets();
    } catch (err) {
      setError(`Failed to clean invalid tickets: ${err?.message}`);
    } finally {
      setCleaning(false);
    }
  };

  // Status options based on role
  const statusOptions = visibleStatuses || [
    'Pending Team Assignment', 'Calculated', 'Pending Approval',
    'Approved', 'On Hold', 'Ready for Payment', 'Paid', 'Rejected', 'Cancelled'
  ];

  const handleDeleteTicket = async () => {
    if (!deleteTarget) return;
    if (!isAdmin && !isProjectManager && !hasPermission(MODULE_KEYS?.INCENTIVE_TICKETS, PERM_KEYS?.DELETE)) {
      setError('You do not have permission to delete tickets.');
      setDeleteTarget(null);
      return;
    }
    setDeleting(true);
    setError('');
    try {
      const result = await deleteIncentiveTicket(deleteTarget?.id, user?.id, user, userProfile);
      setSuccessMsg(
        result?.calculatorRolledBack
          ? `Ticket ${result?.ticketNumber} deleted. Calculator rolled back to Open state.`
          : `Ticket ${result?.ticketNumber} deleted successfully.`
      );
      setDeleteTarget(null);
      await loadTickets();
    } catch (err) {
      setError(err?.message || 'Failed to delete ticket');
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <MainLayout>
      <div className="w-full max-w-7xl mx-auto space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              Project Incentive Tickets
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Close Won deals — incentive tickets only
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <span className="text-sm text-muted-foreground">{tickets?.length} ticket{tickets?.length !== 1 ? 's' : ''}</span>
            {isAdmin && (
              <Button
                variant="outline"
                iconName="ShieldAlert"
                iconPosition="left"
                onClick={handleCleanInvalidTickets}
                disabled={cleaning}
                title="Admin only: Find and mark invalid tickets (linked to non-Close-Won calculations)"
                className="border-warning/50 text-warning hover:bg-warning/10"
              >
                {cleaning ? 'Cleaning...' : 'Clean Invalid Tickets'}
              </Button>
            )}
            <Button variant="outline" iconName="RefreshCw" iconPosition="left" onClick={loadTickets}>
              Refresh
            </Button>
          </div>
        </div>

        {/* Role notice for non-admin */}
        {userRole === 'finance' && (
          <div className="flex items-center gap-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-xl text-blue-400 text-sm">
            <Icon name="Info" size={16} />
            <span>Finance view: Showing Approved, Ready for Payment, and Paid tickets only.</span>
          </div>
        )}

        {/* Success Message */}
        {successMsg && (
          <div className="flex items-start gap-3 p-4 bg-success/10 border border-success/30 rounded-xl text-success text-sm">
            <Icon name="CheckCircle" size={16} className="mt-0.5 shrink-0" />
            <span>{successMsg}</span>
            <button onClick={() => setSuccessMsg('')} className="ml-auto shrink-0 hover:opacity-70">
              <Icon name="X" size={14} />
            </button>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="flex items-start gap-3 p-4 bg-error/10 border border-error/30 rounded-xl text-error text-sm">
            <Icon name="AlertCircle" size={16} className="mt-0.5 shrink-0" />
            <span>{error}</span>
            <button onClick={() => setError('')} className="ml-auto shrink-0 hover:opacity-70">
              <Icon name="X" size={14} />
            </button>
          </div>
        )}

        {/* Filters */}
        <div className="bg-card border border-border rounded-2xl p-4 shadow-sm">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search by ticket number, project, or client..."
                value={search}
                onChange={(e) => setSearch(e?.target?.value)}
                className="w-full pl-9 pr-4 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e?.target?.value)}
              className="px-3 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 min-w-[180px]"
            >
              <option value="">All Statuses</option>
              {statusOptions?.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              <span className="text-sm text-muted-foreground">Loading tickets...</span>
            </div>
          </div>
        ) : tickets?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <div className="p-4 rounded-2xl bg-muted/50">
              <Icon name="Ticket" size={40} className="text-muted-foreground" />
            </div>
            <div className="text-center">
              <p className="font-semibold text-foreground">No Close Won incentive tickets found</p>
              <p className="text-sm text-muted-foreground mt-1">
                Tickets are created only when a saved calculation is marked as <strong>Close Won</strong>.
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Open Estimate History in the Service Calculator and click <strong>"Mark as Close Won & Create Incentive Ticket"</strong>.
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-2xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Ticket #</th>
                    <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Project</th>
                    <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Client</th>
                    <th className="text-right px-4 py-3 font-semibold text-muted-foreground">Net Profit</th>
                    <th className="text-right px-4 py-3 font-semibold text-muted-foreground">Sales Incentive</th>
                    <th className="text-right px-4 py-3 font-semibold text-muted-foreground">Team Incentive</th>
                    <th className="text-right px-4 py-3 font-semibold text-muted-foreground">Total Incentives</th>
                    <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Deal Status</th>
                    <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Ticket Status</th>
                    <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Created</th>
                    <th className="text-center px-4 py-3 font-semibold text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {tickets?.map((ticket) => (
                    <tr
                      key={ticket?.id}
                      className="hover:bg-muted/20 transition-colors cursor-pointer"
                      onClick={() => navigate(`/incentive-tickets/${ticket?.id}`)}
                    >
                      <td className="px-4 py-3">
                        <span className="font-mono font-semibold text-primary">{ticket?.ticketNumber}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-medium text-foreground truncate max-w-[160px] block">{ticket?.projectName}</span>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{ticket?.clientName || '—'}</td>
                      <td className="px-4 py-3 text-right font-medium text-foreground">
                        {formatOMR(ticket?.finalTargetMarginAmount || ticket?.targetMarginAmount)}
                      </td>
                      <td className="px-4 py-3 text-right text-muted-foreground">
                        {formatOMR(ticket?.salesIncentiveAmount)}
                      </td>
                      <td className="px-4 py-3 text-right text-muted-foreground">
                        {formatOMR(ticket?.projectTeamIncentiveAmount)}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-primary">
                        {formatOMR(ticket?.totalIncentives)}
                      </td>
                      <td className="px-4 py-3">
                        {/* Deal Status: Only Close Won should appear here */}
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-success/15 text-success">
                          Close Won
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_COLORS?.[ticket?.status] || 'bg-muted text-muted-foreground'}`}>
                          {ticket?.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground text-xs">
                        {ticket?.createdAt ? new Date(ticket.createdAt)?.toLocaleDateString('en-GB') : '—'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={(e) => { e?.stopPropagation(); navigate(`/incentive-tickets/${ticket?.id}`); }}
                            className="p-1.5 hover:bg-primary/10 rounded-lg transition-colors"
                            title="View Details"
                          >
                            <Icon name="Eye" size={16} className="text-primary" />
                          </button>
                          {/* Delete — Admin / Project Manager or users with delete permission only, not on Paid tickets */}
                          {(isAdmin || isProjectManager || hasPermission(MODULE_KEYS?.INCENTIVE_TICKETS, PERM_KEYS?.DELETE)) && ticket?.status !== 'Paid' && (
                            <button
                              onClick={(e) => {
                                e?.stopPropagation();
                                setDeleteTarget({ id: ticket?.id, ticketNumber: ticket?.ticketNumber, projectName: ticket?.projectName });
                              }}
                              className="p-1.5 hover:bg-error/10 rounded-lg transition-colors"
                              title="Delete ticket and roll back calculator"
                            >
                              <Icon name="Trash2" size={16} className="text-error" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteTarget && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-card border border-border rounded-2xl shadow-2xl max-w-md w-full">
              <div className="p-5 border-b border-border">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-error/15">
                    <Icon name="Trash2" size={18} className="text-error" />
                  </div>
                  <h3 className="font-semibold text-foreground">Delete Incentive Ticket</h3>
                </div>
              </div>
              <div className="p-5 space-y-3">
                <p className="text-sm text-foreground">
                  Delete ticket <span className="font-mono font-semibold text-primary">{deleteTarget?.ticketNumber}</span> for <span className="font-medium">{deleteTarget?.projectName}</span>?
                </p>
                <div className="p-3 bg-warning/10 border border-warning/30 rounded-xl text-warning text-sm flex items-start gap-2">
                  <Icon name="AlertTriangle" size={16} className="mt-0.5 shrink-0" />
                  <span>
                    This will permanently delete the ticket and <strong>roll back the linked calculator to Open state</strong>, allowing a new ticket to be created.
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">This action is logged in the audit trail and cannot be undone.</p>
              </div>
              <div className="flex items-center justify-end gap-3 p-5 border-t border-border">
                <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
                <Button variant="destructive" iconName="Trash2" iconPosition="left" onClick={handleDeleteTicket} disabled={deleting}>
                  {deleting ? 'Deleting...' : 'Delete & Rollback'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default AllIncentiveTickets;
